import React, { useState, useEffect, memo } from "react";
import {
  Text,
  StyleSheet,
  View,
  TouchableOpacity,
  Alert,
  Platform,
} from "react-native";
import Swipeable from "../../../../lib/react-native-swipeable";
import { useNavigation } from "@react-navigation/native";

import RoundCheckbox from "../../common/RoundCheckbox";

import {
  isWeb,
  getElementModel,
  getGroupColor,
  navigate,
  isBaseCoreElement,
  isReflection,
  isFitness,
  isManager,
} from "../../../../helpers/utils";

import AssociatedElements from "./AssociatedElements";
import Icons from "../../common/icons/index";
import appStyles from "../../../appStyles";
import TutorialsHelper from "../../tutorials/TutorialsHelper";
import ElementIcon from "./ElementIcon";

const ElementItem = ({
  item,
  controller,
  showSelector = false,
  noIcons = false, // hide favorite and checkbox
  onOpen = () => {},
  onClose = () => {},
  onElementSelected = () => {},
  showEditOptions = () => {},
  hideActions = false,
  disabledHighlight = false,
  showIcons = true,
  showAssociations = "all",
  style = {},
  titleStyle = {},
  maxToSelect = 0,
  selectedCount = 0,
  isTrackDetailsPage = false,
}) => {
  const [showAllAssociated, setShowAllAssociated] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isSelectable, setIsSelectable] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);

  const isMajor = item.is_major === 1;
  const isCoreElement = item.is_core === 1;

  const showTutorials = controller?.state?.showTutorials;
  const navigation = useNavigation();

  // Base Core Element
  const isBaseGroup = isBaseCoreElement(item.name);
  const mainSetColor = getGroupColor(isBaseGroup ? item.name : item.main_set);

  const swipeActions = hideActions
    ? null
    : [
        <TouchableOpacity
          style={[styles.buttonsContainer, styles.tagAction]}
          onPress={() => editElement()}
        >
          <Text style={styles.buttonText}>Edit</Text>
        </TouchableOpacity>,
        <TouchableOpacity
          style={[styles.buttonsContainer, styles.archiveAction]}
          onPress={() => onArchive(item)}
        >
          <Text style={styles.buttonText}>Archive</Text>
        </TouchableOpacity>,
      ];

  const bgStyle =
    isMajor || isCoreElement
      ? {
          backgroundColor: "#f3f2f1",
          borderRadius: 100,
        }
      : {};

  useEffect(() => {
    setIsFavorite(item.is_favorite == 1);
    setIsSelected(item.is_selected == 1);
    setIsShared(item.is_shared == 1);
    setIsSelectable(!!showSelector || maxToSelect > 0);
  }, [item, maxToSelect]);

  useEffect(() => {
    if (controller?.state?.showTutorials) {
      const showGoalTutorials = (controller?.state?.showTutorials || {})[
        TutorialsHelper.STORAGE_KEYS.goals
      ];

      const showTrackTutorials = (controller?.state?.showTutorials || {})[
        TutorialsHelper.STORAGE_KEYS.track
      ];

      if (showGoalTutorials && !disabledHighlight) {
        setIsHighlighted(isReflection(item.name));
      }

      if (showTrackTutorials) {
        setIsHighlighted(isFitness(item.name));
      }
    }
  }, [controller?.state?.showTutorials]);

  const onElementSelectedHandler = () => {
    if (showTutorials && !isHighlighted) return;

    // Allow a max number of elements to be selected (eg. Track Details page)
    if (
      isSelectable &&
      !(!isSelected && selectedCount > 0 && selectedCount >= maxToSelect)
    ) {
      setIsSelected(!isSelected);
    }

    onElementSelected(
      {
        ...item,
        isSelected: !isSelected,
        isHighlighted,
      },
      !isSelected,
    );
  };

  const showSharedUsers = () => {
    const allowShare = isManager(controller?.state?.teams);

    if (allowShare && item && item.is_owner) {
      navigate(controller, "rn:share_element", "changepage", {
        element: getElementModel(item),
        selection: item.shares,
      });
    }
  };

  const onLongPress = (e) => {
    e.preventDefault();

    if (showEditOptions) {
      showEditOptions(item);
    } else {
      setShowAllAssociated(true);
    }
  };

  const toggleFavoriteState = () => {
    setIsFavorite(!isFavorite);

    controller.tracking.track("Edit Element");
    controller.cardAction({
      action: "save",
      target: "element",
      edit_id: item.id,
      edit_is_active: true,
      edit_is_favorite: !isFavorite,
      edit_name: item.name,
    });
  };

  const editElement = () => {
    //using push to force changing the page if already there
    if (navigation) {
      navigation.push("edit_element", {
        selectedPage: "rn:edit_element",
        args: {
          element: getElementModel(item),
        },
      });
    } else {
      navigate(controller, "rn:edit_element", "changepage", {
        element: getElementModel(item),
      });
    }
  };

  const onArchive = (element) => {
    if (isWeb) {
      confirmedArchive();
      return;
    }

    Alert.alert(
      "Archive element",
      "Are you sure you want to remove this element?",
      [
        {
          text: "Cancel",
          style: "cancel",
        },
        {
          text: "OK",
          onPress: confirmedArchive,
        },
      ],
      { cancelable: true },
    );
  };

  const confirmedArchive = () => {
    controller.tracking.track("Archive Element");
    console.log("Archive element");
    controller.cardActionWithCallback(
      {
        action: "save",
        target: "element",
        edit_id: item.id,
        edit_name: item.name,
        edit_is_active: false,
      },
      () => {
        controller.cardAction({ action: "refresh" });
      },
    );
  };

  return (
    <Swipeable
      rightButtons={swipeActions}
      onRightButtonsOpenRelease={onOpen}
      onRightButtonsCloseRelease={onClose}
    >
      <View
        style={[styles.listItem, style, isHighlighted && appStyles.highlight]}
      >
        <TouchableOpacity
          style={[styles.row, bgStyle]}
          onLongPress={onLongPress}
          onPress={onElementSelectedHandler}
        >
          {showIcons ? <ElementIcon
            element={item}
            icon={item.icon}
            style={styles.elementIcon}
            onPress={onElementSelectedHandler}
          /> : null}

          <View style={{ flex: 1 }}>
            {/* Title */}
            <View
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
              }}
            >
              <Text
                numberOfLines={1}
                style={[
                  styles.itemName,
                  titleStyle,
                  isCoreElement ? { color: mainSetColor } : {},
                ]}
              >
                {item.name}
              </Text>
              {isTrackDetailsPage && isFavorite ? (
                <Icons.Favorite
                  isFavorite={true}
                  style={{ width: 12, height: 12 }}
                  width={12}
                  height={12}
                />
              ) : null}
            </View>

            <AssociatedElements
              mainSetColor={mainSetColor}
              element={item}
              controller={controller}
              showAll={showAllAssociated}
              showAssociations={showAssociations}
            />
          </View>
        </TouchableOpacity>

        {/* Icons */}
        {noIcons ? null : (
          <View style={[styles.icons]}>
            {isSelectable ? (
              <RoundCheckbox
                size={16}
                checked={isSelected}
                onValueChange={onElementSelectedHandler}
              />
            ) : (
              <>
                {isShared ? <Icons.Shared onPress={showSharedUsers} /> : null}

                {isTrackDetailsPage ? null : (
                  <Icons.Favorite
                    isFavorite={isFavorite}
                    toggleFavoriteState={toggleFavoriteState}
                  />
                )}
              </>
            )}
          </View>
        )}
      </View>
    </Swipeable>
  );
};

export default memo(ElementItem);

const styles = StyleSheet.create({
  listItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: 40,
    borderBottomColor: "#F5F5F5",
    borderBottomWidth: 1,
    paddingLeft: 8,
  },
  row: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    flex: 1,
    height: 36,
    paddingLeft: 8,
  },
  itemName: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    color: "#2D3142",
    marginRight: 6,
    ...appStyles.wordOverflow,
  },
  setName: {
    color: "#FFF",
  },
  elementIcon: {
    width: 20,
    height: 20,
    marginRight: 6,
  },
  icons: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    width: 80,
    height: 40,
  },
  buttonsContainer: {
    height: "100%",
    display: "flex",
    justifyContent: "center",
    paddingLeft: 10,
  },
  tagAction: {
    backgroundColor: "#F2994A",
  },
  archiveAction: {
    backgroundColor: "#F34213",
  },
  buttonText: {
    color: "#FFF",
    fontFamily: Platform.select({
      default: "Nunito",
      android: "Nunito-SemiBold",
    }),
    fontWeight: "bold",
    fontSize: 15,
    lineHeight: 20,
  },
});
